import { useCallback, useEffect, useState } from "react";
import { toast } from "@-/com.npmjs.sonner";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useReadinessDeadline } from "@-/dev.improve.haniverse.lib.readiness-handshake";

/**
 * How long a focused, resting current card may wait for its VISIBLE surface to report ready before
 * it is rebuilt. Readiness gates no action; this repairs a card that never drew — a surface stuck
 * behind a lost layout callback, a readiness reset that cleared a face nobody re-mounts. A layout
 * report takes milliseconds and a local read tens of them; this leaves room for a slow first
 * network read of reference data while still ending well before a learner gives up on the card.
 */
export const LEARN_CARD_READINESS_DEADLINE_MS = 8000;
/** Remounts of the current card from scratch before its readiness gate is abandoned. */
export const LEARN_CARD_READINESS_REBUILDS = 1;

/**
 * Recover stranded controls without remounting prepared cards on promotion or Undo, and repair a
 * current card whose visible surface never reports ready: after
 * {@link LEARN_CARD_READINESS_DEADLINE_MS} the card is remounted (a fresh mount re-reports every
 * surface), and if that deadline passes again the learner is told the card did not finish loading.
 * Its actions never waited for any of this.
 */
export function useLearnCardRecovery({
  hasControls,
  isAtRest = true,
  isCurrentCardReady = true,
  isCurrentCardRendered,
  isFocused,
  presentationKey,
}: {
  hasControls: boolean;
  /** Idle or dragging, with no history restore in flight: the only phases that wait on readiness. */
  isAtRest?: boolean;
  /** Whether the surface the current card shows (its face, or Info) has reported ready. */
  isCurrentCardReady?: boolean;
  isCurrentCardRendered: boolean;
  isFocused: boolean;
  presentationKey: string;
}): { getCardRenderKey: (key: string) => string } {
  const [attempts, setAttempts] = useState<Readonly<Record<string, number>>>({});
  const remount = useCallback((key: string) => {
    setAttempts((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }));
  }, []);
  useEffect(() => {
    // Blurred decks intentionally release views and prepared cards. Remounting there is not
    // recovery: Fabric has hidden its native view, so RNGH's attachment retries can expire
    // before the tab returns, leaving an enabled recognizer that receives no touches.
    if (!isFocused || hasControls || !isCurrentCardRendered) {
      return;
    }
    // Allow ordinary promotion and short Suspense transitions to restore the registrar first.
    const timer = setTimeout(() => {
      logger(import.meta.location).warn(
        "Learn deck card rendered without controls; remounting to recover",
        { presentationKey },
      );
      remount(presentationKey);
    }, 2000);
    return () => clearTimeout(timer);
  }, [hasControls, isCurrentCardRendered, isFocused, presentationKey, remount]);

  // Rebuilds spent on THIS presentation's readiness. Keyed by presentation, so a new card starts
  // with its full budget; cleared once the card reports ready, so a later reset starts over too.
  const [readinessRebuilds, setReadinessRebuilds] = useState<{
    count: number;
    presentationKey: string;
  } | null>(null);
  const rebuildCount =
    readinessRebuilds?.presentationKey === presentationKey ? readinessRebuilds.count : 0;
  useEffect(() => {
    if (isCurrentCardReady) {
      setReadinessRebuilds((current) => (current === null ? current : null));
    }
  }, [isCurrentCardReady]);
  // A blurred deck releases its surfaces on purpose, and a committed card is owned by the
  // transition's own deadlines, so only a focused card at rest waits on this one.
  const isAwaitingReadiness = isFocused && isAtRest && !isCurrentCardReady;
  useReadinessDeadline({
    attempt: `${presentationKey}#${rebuildCount}`,
    deadlineMs: LEARN_CARD_READINESS_DEADLINE_MS,
    onTimeout: () => {
      if (rebuildCount < LEARN_CARD_READINESS_REBUILDS) {
        logger(import.meta.location).warn(
          "Learn deck current card never reported ready; remounting to recover",
          { presentationKey, rebuilds: rebuildCount },
        );
        remount(presentationKey);
        setReadinessRebuilds({ count: rebuildCount + 1, presentationKey });
        return;
      }
      logger(import.meta.location).error(
        "Learn deck current card never reported ready after rebuilding",
        { presentationKey, rebuilds: rebuildCount },
      );
      toast.error("This card didn’t finish loading", {
        description: "You can still answer it to move on.",
      });
    },
    operation: "Learn deck current card surfaces",
    waiting: isAwaitingReadiness,
  });

  const getCardRenderKey = useCallback((key: string) => `${key}@${attempts[key] ?? 0}`, [attempts]);
  return { getCardRenderKey };
}
